import {
  queryKeys,
  useHttpClient,
  useActiveOrganizationId,
} from "@repo/api-client/react";
import {
  InvoiceActivitiesListResponseSchema,
  InvoiceSchema,
  InvoicesListResponseSchema,
  type Invoice,
  type InvoiceActivity,
  type UpdateInvoiceRequest,
} from "@repo/api-client/dtos";

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useDebouncedAsyncCallback } from "../internal/useDebouncedAsyncCallback";
import { getJSON, patchJSON } from "../internal/http";

type QueryEnableOptions = { enabled?: boolean };

/**
 * List invoices for the active org. Cursor-paginated under the hood
 * via React Query's `useInfiniteQuery` — the server returns
 * `{ items, nextCursor? }` in 50-row pages and we concatenate them
 * into a single flat array on the way out so the bulk of consumers
 * (which only care about "give me everything") don't have to know
 * the pages exist. Surface the cursor controls
 * (`hasNextPage`, `fetchNextPage`, `isFetchingNextPage`) for the
 * `(tabs)/invoices` `FlatList` that drives `onEndReached`.
 *
 * `pageSize` is fixed at 50 to match the server's
 * `CursorQuerySchema.limit` default; bumping it would require both
 * sides to agree, so we just inline the constant. Smaller is fine
 * (the server caps at 200) but the screens were tuned around 50.
 */
export const INVOICES_PAGE_SIZE = 50;

export function useInvoices(options: QueryEnableOptions = {}) {
  const http = useHttpClient();
  const orgId = useActiveOrganizationId();
  const enabled = options.enabled ?? true;

  const {
    error,
    isPending: loading,
    data,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: orgId ? queryKeys.invoices.all(orgId) : ["invoices", "idle"],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => {
      const search = new URLSearchParams({ limit: String(INVOICES_PAGE_SIZE) });
      if (pageParam) search.set("cursor", pageParam);
      return getJSON(
        http,
        `/orgs/${orgId}/invoices?${search.toString()}`,
        InvoicesListResponseSchema,
      );
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: enabled && !!orgId,
  });

  // Flatten pages → flat list. Memoised so consumers that hash the
  // array reference (e.g. dependency arrays in screens that build
  // per-id lookup maps) don't churn between identical multi-page
  // snapshots.
  const invoices = useMemo<Invoice[] | undefined>(
    () =>
      data
        ? (data.pages.flatMap((page) => page.items) as Invoice[])
        : undefined,
    [data],
  );

  return {
    invoices,
    nextCursor: data?.pages.at(-1)?.nextCursor,
    loading,
    error,
    refetch,
    hasNextPage,
    fetchNextPage: () => fetchNextPage(),
    isFetchingNextPage,
  };
}

export function useInvoice(invoiceId: string, options: QueryEnableOptions = {}) {
  const http = useHttpClient();
  const orgId = useActiveOrganizationId();
  const enabled = options.enabled ?? true;

  const {
    error,
    isPending: loading,
    data: invoice,
    refetch,
  } = useQuery({
    queryKey:
      orgId && invoiceId
        ? queryKeys.invoices.detail(orgId, invoiceId)
        : ["invoices", "idle"],
    queryFn: () =>
      getJSON(http, `/orgs/${orgId}/invoices/${invoiceId}`, InvoiceSchema),
    enabled: enabled && !!orgId && !!invoiceId,
    /**
     * Auto-poll while the durable-send pipeline is mid-flight so a
     * user who re-opens the app on a `pending` invoice doesn't get
     * stuck looking at an indefinite "Sending" pill. We stop the
     * moment the invoice reaches a terminal status (`sent` /
     * `paid` / `cancelled` / `overdue` / `draft`) OR the
     * `finalDocument.status` flips to `'failed'` (so the screen
     * has a chance to render the failure banner without the cache
     * thrashing). The 4s cadence matches the geometric tail in
     * `useSendInvoice` — slow enough to leave room for the worker
     * (most renders flush in 2-10s), fast enough that the pill
     * doesn't feel stale.
     *
     * History: pre-fix, the ONLY refetch trigger was the
     * `useSendInvoice` mutation's tail-off invalidations. If the
     * worker died or the user backgrounded the app and came back,
     * the pill stayed on "Sending" until they pulled to refresh
     * — even though the server already had the answer (whether
     * `sent` or `failed`). The 2026-05-04 incident surfaced this
     * because the underlying error was permanent: nothing was
     * going to flip the status, and there was no UI affordance
     * to drive the retry.
     */
    refetchInterval: (query) => {
      const data = query.state.data as Invoice | undefined;
      if (!data) return false;
      const finalDocStatus = data.finalDocument?.status;
      if (finalDocStatus === 'failed') return false;
      if (data.status === 'pending') return 4_000;
      if (finalDocStatus === 'rendering') return 4_000;
      return false;
    },
  });

  return { invoice: invoice as Invoice | undefined, loading, error, refetch };
}

export function useInvoiceActivities(
  invoiceId: string,
  options: QueryEnableOptions = {},
) {
  const http = useHttpClient();
  const orgId = useActiveOrganizationId();
  const enabled = options.enabled ?? true;

  const {
    error,
    isPending: loading,
    data,
  } = useQuery({
    queryKey:
      orgId && invoiceId
        ? queryKeys.invoices.activities(orgId, invoiceId)
        : ["invoices", "idle"],
    queryFn: () =>
      getJSON(
        http,
        `/orgs/${orgId}/invoices/${invoiceId}/activities`,
        InvoiceActivitiesListResponseSchema,
      ),
    enabled: enabled && !!orgId && !!invoiceId,
  });

  return {
    activities: data?.items as InvoiceActivity[] | undefined,
    loading,
    error,
  };
}

export type UpdateInvoiceVariables = UpdateInvoiceRequest & {
  organizationId: string;
  id: string;
};

/**
 * Optimistic invoice update with cache reconciliation. Splits the
 * URL params (`organizationId`, `id`) from the body — the route is
 * `PATCH /v1/orgs/:orgId/invoices/:invoiceId` and only takes the
 * mutable fields.
 */
export function useUpdateInvoiceMutation() {
  const http = useHttpClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: UpdateInvoiceVariables) => {
      const { organizationId, id, ...updates } = variables;
      return patchJSON(
        http,
        `/orgs/${organizationId}/invoices/${id}`,
        updates,
        InvoiceSchema,
      );
    },
    onMutate: async (variables) => {
      const detailKey = queryKeys.invoices.detail(
        variables.organizationId,
        variables.id,
      );
      const listKey = queryKeys.invoices.all(variables.organizationId);
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previous = queryClient.getQueryData<Invoice>(detailKey);
      if (previous) {
        // Strip the route params off before merging — they aren't
        // fields on the entity. We also drop `lineItems` because the
        // wire shape on the patch input differs from the stored shape;
        // we let the server reconcile and the `onSuccess` write the
        // canonical row back.
        const patch = { ...variables } as Partial<UpdateInvoiceVariables>;
        delete patch.organizationId;
        delete patch.id;
        delete patch.lineItems;
        const optimistic: Invoice = { ...previous, ...patch } as Invoice;
        queryClient.setQueryData<Invoice>(detailKey, optimistic);
        // List cache is now an InfiniteData<{ items, nextCursor? }>
        // since the migration to `useInfiniteQuery` (B1, 2026-05-09).
        // Walk every page so an optimistic patch on row N still shows
        // up regardless of which page the user has scrolled into.
        type InvoicesPage = { items: Invoice[]; nextCursor?: string };
        type InvoicesInfiniteCache = {
          pages: InvoicesPage[];
          pageParams: unknown[];
        };
        queryClient.setQueryData<InvoicesInfiniteCache>(listKey, (cache) =>
          cache
            ? {
                ...cache,
                pages: cache.pages.map((page) => ({
                  ...page,
                  items: page.items.map((inv) =>
                    inv.id === variables.id ? optimistic : inv,
                  ),
                })),
              }
            : cache,
        );
      }
      return { previous };
    },
    onError: (_err, variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          queryKeys.invoices.detail(variables.organizationId, variables.id),
          context.previous,
        );
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        queryKeys.invoices.detail(data.orgId, data.id),
        data,
      );
      queryClient.invalidateQueries({
        queryKey: queryKeys.invoices.all(data.orgId),
      });
      // Any invoice mutation shifts the printer's content hash, so
      // the cached preview/PDF URL is now stale. Invalidate so the
      // detail screen re-fetches and gets the fresh artifact.
      queryClient.invalidateQueries({
        queryKey: queryKeys.printer.preview(data.orgId, data.id),
      });
    },
  });
}

/**
 * Debounced wrapper around `useUpdateInvoiceMutation`. Returns
 * `{ call, flush, retry, pending, error, succeededAt }` — see
 * `useDebouncedUpdateOrganization`
 * for the contract and Phase 6 / plan.md H1 for the rationale.
 */
export function useDebouncedUpdateInvoice(delayMs = 1000) {
  const { mutateAsync } = useUpdateInvoiceMutation();
  return useDebouncedAsyncCallback(
    (vars: UpdateInvoiceVariables) => mutateAsync(vars),
    delayMs,
  );
}

/** Legacy call surface that screens still use. */
export function useUpdateInvoice() {
  const mutation = useUpdateInvoiceMutation();
  return {
    updateInvoice: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}
